import { Box, Dialog, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductService } from "../../../services/api";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import useRefresh from "../../../hooks/useRefresh";
import { Product } from "../../../services/swagger";
import useBreakpoint from "../../../hooks/useBreakpoint";
import useConfirm from "../../../hooks/useConfirm";
import DeleteIcon from "@mui/icons-material/Delete";
import WarehouseInfo from "../../molecules/WarehouseInfo";
import currencies from "../../../assets/currencies.json";

interface EditProductDialogProps {
  product?: Product;
  onSubmit?: () => void;
  onClose?: () => void;
}

export default function EditProductDialog(props: EditProductDialogProps) {
  const { product } = props;
  const { t } = useTranslation("Product");
  const refresh = useRefresh();
  const [currency, setCurrency] = useState("");
  const [group, setGroup] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [extRef, setExtRef] = useState("");
  const confirm = useConfirm();

  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (!product) return;
    setCurrency(product.currency);
    setGroup(product.group);
    setItemCode(product.itemCode);
    setName(product.name);
    setPrice(product.price);
    setExtRef(product.extRef);
  }, [props.product]);

  const handleSubmit = async () => {
    if (!product?._id) return;
    const res = await ProductService.updateProduct({
      id: product._id,
      updateProductBody: {
        currency,
        group,
        itemCode,
        name,
        price,
        extRef,
      },
    });
    if (res?.data?.success) {
      refresh();
      props.onClose?.();
    }
  };

  const handleDelete = () => {
    confirm(async () => {
      if (!product?._id) return;
      const res = await ProductService.deleteProduct({
        id: product._id,
      });
      if (res?.data?.success) {
        refresh();
        props.onClose?.();
      }
    });
  };

  return (
    <Dialog
      fullScreen={!breakpoint.sm}
      open={Boolean(props.product)}
      onClose={props.onClose}
    >
      <FormTemplate
        title={t("EditProduct")}
        onSubmit={handleSubmit}
        onCancel={props.onClose}
        options={[
          {
            icon: <DeleteIcon color="error" />,
            label: t("DeleteProduct"),
            onClick: handleDelete,
          },
        ]}
        confirm
      >
        <Stack spacing={2} sx={{ minWidth: { xs: 1, sm: 400, md: 500 } }}>
          <FormInput
            field="itemCode"
            label={t("ItemCode")}
            value={itemCode}
            type="string"
            onChange={(v) => setItemCode(v as string)}
            required
            readOnly={product?.source === "SAP"}
          />
          <FormInput
            field="name"
            label={t("Name")}
            type="string"
            value={name}
            onChange={(v) => setName(v as string)}
            required
            readOnly={product?.source === "SAP"}
          />
          <FormInput
            field="group"
            label={t("Group")}
            type="string"
            value={group}
            onChange={(v) => setGroup(v as string)}
            required
            readOnly={product?.source === "SAP"}
          />
          <FormInput
            field="price"
            label={t("Price")}
            type="number"
            value={price}
            onChange={(v) => setPrice(v as number)}
            required
            readOnly={product?.source === "SAP"}
          />
          <FormInput
            field="currency"
            label={t("Currency")}
            type="list"
            list={currencies}
            value={currency}
            onChange={(v) => setCurrency(v as string)}
            required
            readOnly={product?.source === "SAP"}
          />
          <FormInput
            field="extRef"
            label={t("ExtRef")}
            type="string"
            value={extRef}
            onChange={(v) => setExtRef(v as string)}
          />

          {product && <WarehouseInfo product={product} />}
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
